import React from "react";
import axios from "axios";
import { Plus, RefreshCw } from "lucide-react";
import { Select } from "../../form/select";
import { Main } from "./style";
import { webURL } from "../../../services/API";

interface itemType { id: string, name: string, info: any, isActive: boolean };
type RawItem = Omit<itemType, 'isActive'>;

// Module-level cache: avoids a fresh API call every time any AuthenticateSelect mounts.
// Key = joined type aliases (e.g., "airtable").  TTL = 2 minutes.
const fetchCache = new Map<string, { raw: RawItem[], ts: number }>();
const CACHE_TTL = 2 * 60 * 1000;

function getConnectionTypeAliases(type: string): string[] {
   if (type === "instagramMsgs") return ["instagramMsgs", "instagram"];
   if (type === "instagramPosting") return ["instagramPosting", "instagram"];
   if (type === "easyOrders") return ["easyOrders", "easyorders"];
   if (type === "bosta") return ["bosta"];
   return [type];
}

const getConnectionId = (connection?: { id?: string; _id?: string } | null): string => {
   if (!connection) return "";
   return connection.id?.trim() || connection._id?.trim() || "";
};

export function AuthenticateSelect(
   { type, defaultId, defaultConnection, handleAuthenticate, withLine, background, withNoneOption }:
   {
      type: string, defaultId: string, handleAuthenticate: (type: 'add' | 'remove', data?: any) => void, withLine?: boolean, background?: string, withNoneOption?: boolean,
      // The node's own already-persisted connection object (id + name + ...info), if any.
      // Used purely as a display fallback — see buildItems below.
      defaultConnection?: { id?: string, name?: string, [key: string]: any } | null,
   }
) {
   const [isLoading, setIsLoading] = React.useState<boolean>(false);
   const [items, setItems] = React.useState<itemType[]>([]);

   // Ref so async callbacks always read the latest defaultId without stale closure
   const defaultIdRef = React.useRef(defaultId);
   defaultIdRef.current = defaultId;
   const defaultConnectionRef = React.useRef(defaultConnection);
   defaultConnectionRef.current = defaultConnection;

   // Build the display list from raw (no isActive) items using the latest defaultId.
   // If the node already has a connection assigned but the fetched/cached connections
   // list doesn't (yet) include it — a fresh fetch still in flight, a stale shared
   // cache, or the connection was removed server-side — synthesize an entry from the
   // node's own stored connection data so the dropdown never shows "unselected" for a
   // connection that's actually attached.
   const buildItems = React.useCallback((raw: RawItem[], activeId: string): itemType[] => {
      const mapped: itemType[] = raw.map(item => ({ ...item, isActive: item.id === activeId }));
      const connectionId = getConnectionId(defaultConnectionRef.current);
      if (activeId && !mapped.some(item => item.id === activeId) && connectionId === activeId) {
         mapped.unshift({ id: activeId, name: defaultConnectionRef.current?.name || 'Current connection', info: defaultConnectionRef.current, isActive: true });
      }
      if (!withNoneOption) return mapped;
      return [{ id: '', name: '— Not set —', info: null, isActive: !activeId }, ...mapped];
   }, [withNoneOption]);

   // Fetch (or read from cache) then update state
   const fetchConnections = React.useCallback(async (bypassCache = false) => {
      setIsLoading(true);
      try {
         const aliases = getConnectionTypeAliases(type);
         const cacheKey = aliases.join(',');
         const hit = fetchCache.get(cacheKey);

         let raw: RawItem[];
         if (!bypassCache && hit && Date.now() - hit.ts < CACHE_TTL) {
            raw = hit.raw;
         } else {
            const responses = await Promise.all(
               aliases.map(name => axios.get(`${webURL}/connections?byName=${name}`))
            );
            const merged = responses
               .flatMap(r => Array.isArray(r.data) ? r.data : [])
               .filter((item, i, self) => self.findIndex((x: any) => x._id === item._id) === i);
            raw = merged.map((item: any, i: number) => ({
               id: item._id, name: item.description || `Connection ${i + 1}`, info: item.info
            }));
            fetchCache.set(cacheKey, { raw, ts: Date.now() });
         }

         const currentId = defaultIdRef.current || getConnectionId(defaultConnectionRef.current);
         setItems(buildItems(raw, currentId));
         if (currentId && !raw.some(r => r.id === currentId)) {
            handleAuthenticate('remove');
         }
      } catch {
         // On error show an empty list — don't stay stuck in loading
         setItems(buildItems([], defaultIdRef.current));
      } finally {
         setIsLoading(false);
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [type, buildItems]);

   // On mount: use cache for instant display, fetch only when cache is cold OR the
   // cached list doesn't include the node's currently-selected connection (e.g. it
   // was picked/created after that cache entry was populated by some other node's
   // AuthenticateSelect — the cache is shared app-wide). Trusting a cache that's
   // missing the active selection is exactly what makes an already-connected node
   // appear unselected after collapsing and reopening its panel.
   React.useEffect(() => {
      const cacheKey = getConnectionTypeAliases(type).join(',');
      const hit = fetchCache.get(cacheKey);
      const cacheIsFresh = hit && Date.now() - hit.ts < CACHE_TTL;
      const cacheHasSelection = !defaultId || (hit && hit.raw.some(r => r.id === defaultId));
      if (cacheIsFresh && cacheHasSelection) {
         // Instant — no loading spinner
         setItems(buildItems(hit!.raw, defaultId));
      } else {
         fetchConnections();
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // When defaultId prop changes (e.g., global connection applied), remap isActive —
   // and inject the fallback entry (see buildItems) if the new id isn't in the list yet.
   React.useEffect(() => {
      const activeId = defaultId || getConnectionId(defaultConnection);
      setItems(prev => {
         const hasMatch = prev.some(item => item.id === activeId);
         const remapped = prev.map(item => ({
            ...item,
            isActive: item.id === '' ? !activeId : item.id === activeId
         }));
         if (activeId && !hasMatch) {
            return [{ id: activeId, name: defaultConnection?.name || 'Current connection', info: defaultConnection, isActive: true }, ...remapped];
         }
         return remapped;
      });
   }, [defaultId, defaultConnection]);

   const handleSelectConnection = (id: string) => {
      if (id === '') {
         setItems(prev => prev.map(item => ({ ...item, isActive: item.id === '' })));
         handleAuthenticate('remove');
         return;
      }
      const updated = items.map(item => ({ ...item, isActive: item.id === id }));
      setItems(updated);
      const find = updated.find(item => item.isActive);
      // Spread info first, then override id/_id with the MongoDB document id so
      // any info.id or info._id field in the raw connection never silently wins.
      handleAuthenticate('add', { ...find?.info, id: find?.id, _id: find?.id, name: find?.name });
   };

   return (
      <Main className={`authSelect ${withLine ? 'withLine' : ''}`}>
         <Select data={ items } emptyMsg="There is no connection" handleSelectItem={ handleSelectConnection } isSmall={ true } />
         <button
            type="button"
            className="btn1 refreshButton"
            onClick={ () => fetchConnections(true) }
            disabled={ isLoading }
            aria-label="Reload connections"
            title="Reload connections"
            style={{ background: background || '#3E82F8' }}
         >
            <RefreshCw size={17} className={isLoading ? 'spinning' : ''} />
         </button>
         <button
            type="button"
            className="btn1 addButton"
            onClick={ () => window.open('/integration-manager', '_blank', 'noopener,noreferrer') }
            style={{ background: background || '#3E82F8' }}
         >
            <Plus size={17} />
            <span>Add new</span>
         </button>
      </Main>
   );
}
